import {
    FlexColumn,
    FlexRow,
    FlexRowBetween,
    LoaderBar,
    PageHeader,
} from 'components'
import { AddInviteModal, GroupList, NotificationGroupModal } from 'page-components'
import { useState } from 'react'
import {
    GetUserListForGroup,
    GetNotificationGroupCustomerList,
    GetGroupList,
    GetTempleteList,
} from 'hooks'
import {
    AuthService,
    DefaultText,
    GroupService,
    InviteType,
    InviteUserService,
    PaginationType,
} from 'utility'
import { AppPrimaryButton } from 'elements'
import styled from 'styled-components'
import { DesktopMode, MobileMode } from 'layout'
import { useNotification } from 'hooks/toast'

const GroupedScreenContainer = styled.div`
    .error {
        color: ${({ theme }) => theme.colors.danger};
    }
`

const toArray = value => (Array.isArray(value) ? value : [])

const extractGroupId = response => {
    if (response == null) return null
    if (typeof response === 'number') return response
    return (
        response?.GroupId ??
        response?.groupId ??
        response?.Data?.GroupId ??
        response?.Record?.GroupId ??
        null
    )
}

const formatApiError = err => {
    const status = err?.status
    const statusSuffix = status ? ` (${status})` : ''
    const body = err?.error

    if (typeof body === 'string' && body.trim() && body !== 'undefined') {
        return `${body}${statusSuffix}`
    }
    if (body?.message) return `${body.message}${statusSuffix}`
    if (body?.Message) return `${body.Message}${statusSuffix}`
    if (typeof body === 'object' && body !== null) {
        const first = Object.values(body).find(
            v => typeof v === 'string' && v.trim() && v !== 'undefined',
        )
        if (first) return `${first}${statusSuffix}`
    }
    if (err?.message && err.message !== 'undefined') {
        return `${err.message}${statusSuffix}`
    }
    if (status === 400) {
        return `Save failed (400). Check the group name and try again.${statusSuffix}`
    }
    return `${DefaultText.Default}${statusSuffix}`
}

const buildTeamGroupHeaderPayload = ({
    groupId,
    groupName,
    autoEmail,
    onHold,
    templateIds,
}) => ({
    GroupId: Number(groupId),
    GroupName: groupName,
    AutoEmail: autoEmail,
    OnHold: onHold,
    IsCapa: false,
    TemplateId: templateIds?.length ? templateIds : null,
})

const saveTeamGroupHeader = async (
    groupId,
    groupName,
    autoEmail,
    onHold,
    templateIds,
) => {
    const payload = buildTeamGroupHeaderPayload({
        groupId,
        groupName,
        autoEmail,
        onHold,
        templateIds,
    })

    try {
        await GroupService.update(payload)
    } catch (postErr) {
        if (postErr?.status === 400) {
            await GroupService.updateTeamGroup(payload)
            return
        }
        throw postErr
    }
}

export const GroupListScreen = () => {
    const companyId = AuthService.getUser()?.CompanyId

    const { successToast, errorToast } = useNotification()
    const {
        data,
        setData,
        loading,
        filter,
        pageChanged,
        totalCount,
        refresh: listrefresh,
    } = GetGroupList()
    const { data: allGroupsData, refresh: allRefresh } =
        GetGroupList(PaginationType.All)
    const { options: templateOptions, data: templatesData } =
        GetTempleteList(PaginationType.All)
    const { options: userseOptions, data: usersData } =
        GetUserListForGroup(PaginationType.All)
    const { options: customerOptions, data: customersData } =
        GetNotificationGroupCustomerList(companyId, { forceStandardList: true })

    const [processing, setProcessing] = useState('')
    const [inviteModal, setinviteModal] = useState(false)
    const [error, setError] = useState('')
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [groupModalOpen, setGroupModalOpen] = useState(false)

    const remove = async id => {
        setProcessing('Processing')
        try {
            await GroupService.remove(id)
            setData(data.filter(ele => ele.GroupId !== id))
            listrefresh()
            allRefresh()
        } catch {
            setError(DefaultText.Default)
        } finally {
            setProcessing('')
        }
    }

    const submitInvites = async formData => {
        try {
            if (formData.InviteTo) {
                await InviteUserService.sendInvitation([
                    {
                        InviteTo: formData.InviteTo,
                        InviteType: InviteType.Email,
                        FullName: formData.FullName,
                        CustomerIds: formData.CustomerIds || null,
                        TemplateIds: formData.TemplateIds || null,
                        RoleId: formData.RoleId || '',
                        AccessMode: formData.AccessMode || 'Inherit',
                        UserPermissionOverrides: formData.UserPermissionOverrides || [],
                        LoginSource: formData.LoginSource || 1,
                        ShowPhotoGps:
                            typeof formData.ShowPhotoGps === 'boolean'
                                ? formData.ShowPhotoGps
                                : true,
                    },
                ])
                successToast('Member invitation has been sent')
            }
        } catch (inviteError) {
            console.log('error', inviteError)
            errorToast('Failed to send invitation')
        } finally {
            setinviteModal(false)
        }
    }

    const handleUnifiedSave = async formData => {
        const {
            groupId: existingGroupId,
            groupName,
            autoEmail,
            onHold,
            userIds = [],
            customerIds = [],
            templateIds = [],
            otherEmails = [],
        } = formData

        try {
            setProcessing('Processing')
            setError('')

            let groupId = existingGroupId

            if (groupId) {
                const duplicate = toArray(allGroupsData).find(
                    g =>
                        g.GroupId !== groupId &&
                        g.GroupName?.toLowerCase().trim() ===
                        groupName.toLowerCase().trim(),
                )
                if (duplicate) {
                    throw new Error(
                        'A notification group with this name already exists.',
                    )
                }

                await saveTeamGroupHeader(
                    groupId,
                    groupName,
                    autoEmail,
                    onHold,
                    templateIds,
                )
            } else {
                const duplicate = toArray(allGroupsData).find(
                    g =>
                        g.GroupName?.toLowerCase().trim() ===
                        groupName.toLowerCase().trim(),
                )
                if (duplicate) {
                    throw new Error(
                        'A notification group with this name already exists.',
                    )
                }

                const created = await GroupService.add({
                    GroupName: groupName,
                    IsCapa: false,
                    AutoEmail: true,
                    OnHold: onHold,
                    TemplateId: templateIds,
                })
                groupId = extractGroupId(created)
                if (!groupId) {
                    throw new Error('Group was created but no GroupId was returned.')
                }
            }

            const memberPayload = {
                GroupId: groupId,
                UserList: userIds.join(','),
                CustomerList: customerIds.join(','),
                TemplateList: templateIds.join(','),
                OtherEmails: otherEmails.join(';'),
            }

            await GroupService.addGroupMembers(memberPayload)

            listrefresh()
            allRefresh()
            setGroupModalOpen(false)
            setSelectedGroup(null)
        } catch (saveError) {
            console.error('Error saving notification group:', saveError)
            setError(formatApiError(saveError))
        } finally {
            setProcessing('')
        }
    }

    const handleEditGroup = group => {
        setError('')
        setSelectedGroup(group)
        setGroupModalOpen(true)
    }

    const openCreateGroupModal = () => {
        setError('')
        setSelectedGroup(null)
        setGroupModalOpen(true)
    }

    const closeGroupModal = () => {
        setGroupModalOpen(false)
        setSelectedGroup(null)
        setError('')
    }

    return (
        <GroupedScreenContainer>
            <DesktopMode>
                <FlexRowBetween>
                    <PageHeader header="Notification Groups" />
                    <div>
                        <AppPrimaryButton onClick={openCreateGroupModal}>
                            Notification Group
                        </AppPrimaryButton>
                        <AppPrimaryButton onClick={() => setinviteModal(true)}>
                            Invite Member
                        </AppPrimaryButton>
                    </div>
                </FlexRowBetween>
            </DesktopMode>
            <MobileMode>
                <FlexColumn>
                    <PageHeader header="Notification Groups" />
                    <FlexRow>
                        <AppPrimaryButton fluid onClick={openCreateGroupModal}>
                            Notification Group
                        </AppPrimaryButton>
                        <AppPrimaryButton
                            fluid
                            onClick={() => setinviteModal(true)}
                        >
                            Invite Member
                        </AppPrimaryButton>
                    </FlexRow>
                </FlexColumn>
            </MobileMode>
            <AddInviteModal
                loading={processing}
                open={inviteModal}
                onClose={submitInvites}
            />
            <NotificationGroupModal
                open={groupModalOpen}
                onClose={closeGroupModal}
                onSubmit={handleUnifiedSave}
                loading={Boolean(processing)}
                initialGroup={selectedGroup}
                apiError={error}
                templateOptions={templateOptions}
                customerOptions={customerOptions}
                userOptions={userseOptions}
                usersData={toArray(usersData)}
                customersData={toArray(customersData)}
                templatesData={toArray(templatesData)}
            />
            <div className="my-3">
                {(processing || loading) && (
                    <LoaderBar content={processing || 'Loading'} />
                )}
                {error && <div className="error my-2">{error}</div>}
                <GroupList
                    data={data}
                    pageChanged={pageChanged}
                    totalCount={totalCount}
                    filter={filter}
                    remove={remove}
                    onEdit={handleEditGroup}
                />
            </div>
        </GroupedScreenContainer>
    )
}
